import React, { useCallback, useMemo, useRef, useState } from 'react';

import dayjs from 'dayjs';
import saveAs from 'file-saver';
import { HttpStatusCode } from 'axios';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { TaskEditSearchPanel } from './search_panel';

import { SpreadSheet } from '../../../lib/spreadsheet/spreadsheet';
import { useTaskEditStore } from './task_edit_store';
import { CsvListRequest } from '../../../model/csvlist_request';
import { DataType, DataTypeKeys, DateTypeKeys, SourceKeys } from '../../../constant';
import { SpreadSheetData } from '../../../model/spread_sheet_response';
import { ApiResponse } from '../../../api/api';
import { Messages } from '../../../constant/messages';
import { useIndicator } from '../../../hook/use_indicator';
import { useSessionStore } from '../../../context/session';
import { PermissionName } from '../../../valueobject/permissionName';
import { RowValue } from '../../../lib/spreadsheet/types';
/**
 * 作業計画参照・更新画面
 * @constructor
 */
export const TaskEditView : React.FC = () => {
  const navigator = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const [request, setRequest] = useState<CsvListRequest>(null);
  const [response, setResponse] = useState<SpreadSheetData>(null);
  const [dateType, setDateType] = useState<DateTypeKeys>(DateTypeKeys.yearly);
  const [fileName, setFileName] = useState<SourceKeys>(SourceKeys.cleaning_reg);
  const [year, setYear] = useState<number>(2024);
  const [month, setMonth] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const indicator = useIndicator();
  const { hasPermission } = useSessionStore((selector) => selector);
  const [messageApi, contextHolder] = message.useMessage();

  // ストア情報
  const { getCsvList, getCsvDownload } = useTaskEditStore((selector) => selector);

  const search = useCallback((data: CsvListRequest, page: number) => {
    setResponse(null);
    setFileName(data.targetFile);
    setYear(data.year);
    setMonth(data.month);
    setDateType(data.dateType);
    setPage(page);

    indicator.show();
    getCsvList({ ...data, page }).then((resp) => {
      setResponse(resp);
    }).catch((e) => {
      const { status } = e.response;
      if (status === HttpStatusCode.Unauthorized || status === HttpStatusCode.Forbidden) {
        window.alert(Messages.ERR_DIALOG_RETURN_LOGIN);
        navigator('/login', { state: { message: Messages.ERR_401 } });
      } else if (status === -1) {
        window.alert(Messages.ERR_DIALOG_TIMEOUT);
      } else if (status === HttpStatusCode.Conflict) {
        window.alert(Messages.ERR_DIALOG_CSV_CONFLICT);
      } else {
        window.alert(Messages.ERR_DIALOG_RETRY_OPERATION);
      }
    }).finally(() => {
      indicator.hide();
    });
  }, []);

  const handleSearch = useCallback((type: 'search' | 'download', data: CsvListRequest) => {
    if (type !== 'search') {
      // ダウンロード
      indicator.show();
      getCsvDownload(data).then(
        (res: ApiResponse) => {
          console.log('res：', res);
          // @ts-ignore
          const contentDisposition = res.headers['content-disposition'];
          let fileName = 'default.csv'; // デフォルトのファイル名
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="(.+)"/);
            if (match && match[1]) {
              fileName = decodeURIComponent(match[1]);
            }
          }
          // @ts-ignore
          const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
          saveAs(blob, `${fileName}`);
        },
      ).catch((e) => {
        const { status } = e.response;
        if (status === HttpStatusCode.Unauthorized || status === HttpStatusCode.Forbidden) {
          window.alert(Messages.ERR_DIALOG_RETURN_LOGIN);
          navigator('/login', { state: { message: Messages.ERR_401 } });
        } else if (status === -1) {
          window.alert(Messages.ERR_DIALOG_TIMEOUT);
        } else if (status === HttpStatusCode.Conflict) {
          window.alert(Messages.ERR_DIALOG_CSV_CONFLICT);
        } else {
          window.alert(Messages.ERR_DIALOG_RETRY_OPERATION);
        }
      }).finally(() => {
        indicator.hide();
      });
      return;
    }

    // 検索
    setRequest(data);
    search(data, 1);
  }, [search]);

  const onUpdate = useCallback((_: RowValue[], pager: number) => {
    messageApi.success(Messages.EXECUTE_ISSUE_UPDATE);
    search(request, pager);
  }, [request, search]);

  const tooltip = useMemo(() => {
    if (response === null || response === undefined) {
      return null;
    }
    const tips = response.tips?.split('\n') ?? [];
    return (
        <>
          {tips.map((tip, index) => (
              <div key={index}>{tip}</div>
          ))}
        </>
    );
  }, [response]);

  return (
        <div ref={ref}>
            {contextHolder}
            <h1>作業計画参照・更新画面</h1>
            <TaskEditSearchPanel onSearch={handleSearch}/>

          {response !== null && (
              <div>
                <SpreadSheet
                    historyId={response.historyId}
                    targetFile={fileName}
                    dateType={dateType}
                    count={response.count}
                    year={year}
                    month={month}
                    showId={true}
                    page={page}
                    headers={response.headers}
                    toolTip={tooltip}
                    freezeX={8}
                    parPage={50}
                    receiveApi={request}
                    onUpdate={onUpdate}
                    lastModified={dayjs()}
                    editable={hasPermission(PermissionName.WORK_PLAN, true) && response.editable}
                    initialRows={response.rows}/>
              </div>
          )}
        </div>
  );
};
